import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getProjectById } from '../services/projectService';
import { FaGithub, FaArrowLeft, FaCheckCircle, FaStar, FaCodeBranch, FaTools } from 'react-icons/fa';
import * as FaIcons from 'react-icons/fa';
import * as SiIcons from 'react-icons/si';

const ProjectDetails = () => {
  const { id } = useParams();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchProject = async () => {
        try {
            const data = await getProjectById(id);
            if (data) {
                setProject(data);
            }
        } catch (err) {
            console.error('Error fetching project:', err);
            setError('Project not found or server error.');
        } finally {
            setLoading(false);
        }
    };
    fetchProject();
  }, [id]);

  if (loading) {
    return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
             <div className="loading-spinner w-12 h-12 border-4 border-indigo-600 border-t-transparent"></div>
        </div>
    );
  }

  if (error || !project) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 gap-4">
        <h2 className="heading-secondary text-gray-900">{error || 'Project Not Found'}</h2>
        <Link to="/open-source" className="px-6 py-2 bg-indigo-600 text-white rounded-lg font-bold hover:bg-indigo-700">Back to Projects</Link>
      </div>
    );
  }

  // Dynamic Icon rendering helper
  const renderIcon = (iconName, colorClass) => {
    // If iconName is just 'FaCode' or similar string
    const trimIcon = iconName ? iconName.trim() : 'FaTools';
    const IconComponent = FaIcons[trimIcon] || SiIcons[trimIcon] || FaTools;
    return <IconComponent className={`w-6 h-6 ${colorClass || 'text-gray-500'}`} />;
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      
      {/* Header Banner */}
      <div className="bg-gradient-to-r from-gray-900 via-slate-800 to-gray-900 pb-24 pt-32 px-4 relative overflow-hidden">
          <div className="absolute inset-0 opacity-10 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')]"></div>
          <div className="absolute top-0 right-0 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl"></div>

          <div className="container-responsive relative z-10 text-center sm:text-left">
              <Link to="/open-source" className="inline-flex items-center text-blue-200 hover:text-white mb-8 transition-colors">
                  <FaArrowLeft className="mr-2" /> Back to Projects
              </Link>
              
              <div className="flex flex-col sm:flex-row items-center sm:items-start justify-between gap-6">
                  <div>
                      <h1 className="heading-primary mb-4 text-white">{project.title}</h1>
                      <div className="flex flex-wrap justify-center sm:justify-start gap-4 text-gray-300 mb-6">
                           <div className="flex items-center space-x-2 bg-white/10 px-3 py-1.5 rounded-lg border border-white/10">
                              <FaStar className="text-yellow-400" />
                              <span className="font-bold">{project.stars} Stars</span>
                           </div>
                           <div className="flex items-center space-x-2 bg-white/10 px-3 py-1.5 rounded-lg border border-white/10">
                              <FaCodeBranch className="text-gray-400" />
                              <span className="font-bold">{project.forks} Forks</span>
                           </div>
                           <div className={`px-3 py-1.5 rounded-lg font-bold border ${
                               project.difficulty === 'Beginner' ? 'bg-green-500/20 text-green-300 border-green-500/30' :
                               project.difficulty === 'Intermediate' ? 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30' :
                               'bg-red-500/20 text-red-300 border-red-500/30'
                           }`}>
                               {project.difficulty}
                           </div>
                      </div>
                  </div>
                  
                  <a href={project.githubLink} target="_blank" rel="noopener noreferrer" className="px-6 py-3 bg-white text-gray-900 rounded-xl font-bold hover:bg-gray-100 transition-all shadow-lg flex items-center shrink-0">
                      <FaGithub className="mr-2 text-xl" /> Contribute Now
                  </a>
              </div>
          </div>
      </div>

      <div className="container-responsive -mt-16 px-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              
              {/* Main Content */}
              <div className="lg:col-span-2 space-y-8">
                  
                  {/* About */}
                  <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
                      <h2 className="heading-tertiary text-gray-900 mb-6">About Project</h2>
                      <p className="text-gray-600 leading-relaxed text-lg">
                          {project.description}
                      </p>
                  </div>

                  {/* Guidelines */}
                  {project.guidelines && project.guidelines.length > 0 && (
                    <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
                      <h2 className="text-2xl font-bold text-gray-900 mb-6">Contribution Guidelines</h2>
                      
                      <div className="space-y-4">
                          {project.guidelines.map((step, idx) => (
                              <div key={idx} className="flex items-start">
                                  <div className="flex-shrink-0 w-8 h-8 bg-blue-50 text-blue-600 rounded-full flex items-center justify-center font-bold text-sm mr-4 mt-0.5">
                                      {idx + 1}
                                  </div>
                                  <p className="text-gray-700">{step}</p>
                              </div>
                          ))}
                      </div>
                    </div>
                  )}

                  {/* Rules */}
                  {project.rules && project.rules.length > 0 && (
                    <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
                        <h2 className="text-2xl font-bold text-gray-900 mb-6">Rules & Conduct</h2>
                        <ul className="space-y-3">
                            {project.rules.map((rule, idx) => (
                                <li key={idx} className="flex items-center text-gray-700">
                                    <FaCheckCircle className="text-green-500 mr-3 shrink-0" />
                                    {rule}
                                </li>
                            ))}
                        </ul>
                    </div>
                  )}

              </div>

              {/* Sidebar */}
              <div className="space-y-8">
                  
                  {/* Tech Stack */}
                  <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                      <h3 className="text-lg font-bold text-gray-900 mb-4">Tech Stack</h3>
                      <div className="flex flex-col gap-3">
                          {project.techStack.length > 0 ? project.techStack.map((tech, idx) => (
                              <div key={idx} className="flex items-center p-3 bg-gray-50 rounded-xl">
                                  <div className="mr-3">
                                      {renderIcon(tech.icon, tech.color)}
                                  </div>
                                  <span className="font-medium text-gray-700">{tech.name}</span>
                              </div>
                          )) : (
                              <p className="text-gray-500 text-sm">No tech stack specified.</p>
                          )}
                      </div>
                  </div>

                  {/* Repo Details */}
                  <div className="bg-gradient-to-br from-gray-900 to-slate-800 rounded-2xl p-6 shadow-lg text-white relative overflow-hidden">
                      <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2 blur-2xl"></div>
                      <h3 className="text-lg font-bold mb-4 z-10 relative">Ready to code?</h3>
                      <p className="text-gray-300 text-sm mb-6 z-10 relative">
                          Clone the repository and start creating a pull request today.
                      </p>
                      <a 
                        href={project.githubLink} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="w-full block text-center py-3 bg-white text-gray-900 rounded-xl font-bold hover:bg-gray-100 transition-colors z-10 relative"
                      >
                          Go to Repository
                      </a>
                  </div>

              </div>

          </div>
      </div>
    </div>
  );
};

export default ProjectDetails;
